import {
  DATA_EXTRACTION_PROPERTY_TYPES,
  type DataExtractionPropertyFormValue,
  isEnumSupportedType,
} from '@/lib/data-extraction';

export type PropertyDraftErrors = Partial<
  Record<'identifier' | 'arrayItemType' | 'enumValues', string>
>;

export const ARRAY_ITEM_TYPE_OPTIONS = DATA_EXTRACTION_PROPERTY_TYPES.filter(
  type => type !== 'array'
);

export const formatPropertyTypeLabel = (property: DataExtractionPropertyFormValue) => {
  if (property.dataType !== 'array') {
    return property.dataType;
  }

  return `array<${property.arrayItemType ?? 'string'}>`;
};

export const normalizeEnumValues = (value: string) =>
  value
    .split(/\r?\n|,/)
    .map(item => item.trim())
    .filter(Boolean);

export const getEnumValidationMessage = (
  enumValues: string[],
  property: DataExtractionPropertyFormValue
) => {
  const targetType = property.dataType === 'array' ? property.arrayItemType : property.dataType;

  if (!targetType || !isEnumSupportedType(targetType) || enumValues.length === 0) {
    return undefined;
  }

  if (targetType === 'number' && enumValues.some(value => !Number.isFinite(Number(value)))) {
    return 'Number enums must use valid numeric values';
  }

  if (targetType === 'integer' && enumValues.some(value => !/^-?\d+$/.test(value))) {
    return 'Integer enums must use whole numbers';
  }

  if (
    targetType === 'boolean' &&
    enumValues.some(value => !['true', 'false'].includes(value.toLowerCase()))
  ) {
    return 'Boolean enums can only contain true or false';
  }

  return undefined;
};

export const getEnumHint = (property: DataExtractionPropertyFormValue) => {
  const targetType = property.dataType === 'array' ? property.arrayItemType : property.dataType;

  if (targetType === 'number') {
    return 'Enter one numeric value per line.';
  }

  if (targetType === 'integer') {
    return 'Enter one whole number per line.';
  }

  if (targetType === 'boolean') {
    return 'Allowed values are true or false.';
  }

  return 'Enter one allowed value per line.';
};
